/**
 * Deterministic retrieval over the knowledge base.
 *
 * No model, no network, no generation. A question is scored against every entry
 * by token overlap; the best entry wins only if it clears a confidence floor,
 * and otherwise the widget says it doesn't know and offers a human. That last
 * behaviour is the important one — the failure mode we are designing against is
 * a confident wrong answer about a price or a policy, not an unanswered
 * question.
 *
 * The interface here (`findAnswer` returning a scored, possibly-empty match) is
 * deliberately the same shape a learned retriever would return, so Phase 2 can
 * replace the scoring function without touching the widget.
 */
import { KNOWLEDGE } from "./knowledge";

/** Words that carry no retrieval signal in this domain. */
const STOP = new Set([
  "a", "an", "and", "are", "as", "at", "be", "can", "could", "do", "does", "for",
  "from", "get", "give", "has", "have", "how", "i", "if", "in", "is", "it", "its",
  "me", "my", "of", "on", "or", "please", "should", "so", "tell", "that", "the",
  "their", "them", "then", "there", "these", "they", "this", "to", "us", "want",
  "was", "we", "were", "what", "when", "where", "which", "who", "will", "with",
  "would", "you", "your", "hi", "hello", "hey", "thanks", "thank",
]);

/**
 * Crude suffix stripping — enough to tie "cones"/"cone", "charges"/"charge".
 *
 * The "es" rule only applies after a sibilant ("boxes" -> "box"). Applying it
 * unconditionally turned "cones" into "con", which then matched nothing.
 */
function stem(w) {
  if (w.length > 4 && w.endsWith("ies")) return w.slice(0, -3) + "y";
  if (w.length > 4 && w.endsWith("es") && /[sxzh]$/.test(w.slice(0, -2))) {
    return w.slice(0, -2);
  }
  if (w.length > 3 && w.endsWith("s") && !w.endsWith("ss")) return w.slice(0, -1);
  return w;
}

export function tokenize(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9₹\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w && !STOP.has(w))
    .map(stem);
}

/**
 * How well one entry answers one question, as a 0–1 score.
 *
 * Keywords are the primary signal. `asks` are weighted lower but reward a
 * question phrased close to one we anticipated. Dividing by the question's own
 * length stops a long rambling question from scoring highly against everything
 * just by containing more words.
 */
function score(qTokens, entry) {
  if (!qTokens.length) return 0;

  const kw = new Set(entry.keywords.map(stem));
  const askTokens = new Set(entry.asks.flatMap(tokenize));
  const titleTokens = new Set(tokenize(entry.title));

  let hits = 0;
  for (const t of qTokens) {
    if (kw.has(t)) hits += 1.0;
    else if (titleTokens.has(t)) hits += 0.7;
    else if (askTokens.has(t)) hits += 0.5;
  }
  return hits / qTokens.length;
}

/**
 * Below this, the widget refuses to answer and offers a human instead.
 *
 * Tuned against the cases in match.test.js. Raising it makes the widget hand
 * off more often, which is the safe direction — every handoff is a WhatsApp
 * message you were already getting today, whereas a wrong answer is new damage.
 */
export const CONFIDENCE_FLOOR = 0.34;

export function findAnswer(question) {
  const qTokens = tokenize(question);
  const ranked = KNOWLEDGE
    .map((entry) => ({ entry, score: score(qTokens, entry) }))
    .sort((a, b) => b.score - a.score);

  const best = ranked[0];
  const runnerUp = ranked[1];

  if (!best || best.score < CONFIDENCE_FLOOR) {
    return { entry: null, score: best ? best.score : 0, confident: false, alternatives: [] };
  }

  // Two entries scoring nearly the same means the question was ambiguous
  // ("cone" matches both the cone/stick comparison and the price entry).
  // Offer the runner-up rather than silently picking one.
  const alternatives =
    runnerUp && runnerUp.score >= best.score * 0.8 && runnerUp.score >= CONFIDENCE_FLOOR
      ? [runnerUp.entry]
      : [];

  return { entry: best.entry, score: best.score, confident: true, alternatives };
}
